import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { AlertTriangle, HelpCircle, Trash2 } from 'lucide-react';
import { useEscapeKey } from '../../hooks/useEscapeKey';

export type ConfirmVariant = 'default' | 'danger' | 'warning';

export interface ConfirmDialogProps {
  title: string;
  message?: React.ReactNode;
  confirmText: string;
  cancelText: string;
  variant?: ConfirmVariant;
  /** Quando definido, o diálogo mostra um campo de texto (modo prompt). */
  input?: {
    label?: string;
    placeholder?: string;
    defaultValue?: string;
    required?: boolean;
    multiline?: boolean;
  };
  onConfirm: (value?: string) => void;
  onCancel: () => void;
}

const variantStyles: Record<ConfirmVariant, { icon: React.ReactNode; iconWrap: string; button: string }> = {
  default: {
    icon: <HelpCircle className="w-6 h-6" />,
    iconWrap: 'bg-primary-100 text-primary-600',
    button: 'bg-primary-600 hover:bg-primary-700 focus-visible:ring-primary-500'
  },
  danger: {
    icon: <Trash2 className="w-6 h-6" />,
    iconWrap: 'bg-red-100 text-red-600',
    button: 'bg-red-600 hover:bg-red-700 focus-visible:ring-red-500'
  },
  warning: {
    icon: <AlertTriangle className="w-6 h-6" />,
    iconWrap: 'bg-yellow-100 text-yellow-600',
    button: 'bg-yellow-600 hover:bg-yellow-700 focus-visible:ring-yellow-500'
  }
};

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title,
  message,
  confirmText,
  cancelText,
  variant = 'default',
  input,
  onConfirm,
  onCancel
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [value, setValue] = useState(input?.defaultValue ?? '');
  const confirmRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const titleId = useId();
  const messageId = useId();
  const styles = variantStyles[variant];
  const canConfirm = !input?.required || value.trim().length > 0;

  useEscapeKey(onCancel, true);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsVisible(true));
    if (input) {
      inputRef.current?.focus();
      inputRef.current?.select();
    } else {
      confirmRef.current?.focus();
    }
    return () => cancelAnimationFrame(frame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = useCallback(() => {
    if (!canConfirm) return;
    onConfirm(input ? value : undefined);
  }, [canConfirm, input, onConfirm, value]);

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm';

  return (
    <div
      className={`fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 transition-opacity duration-150 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={message ? messageId : undefined}
        className={`bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-xl shadow-2xl transition-all duration-150 ${
          isVisible ? 'opacity-100 translate-y-0 sm:scale-100' : 'opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
        }`}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleConfirm();
          }}
        >
          <div className="px-5 sm:px-6 pt-6 pb-4 flex gap-4">
            <div className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center ${styles.iconWrap}`}>
              {styles.icon}
            </div>
            <div className="flex-1 min-w-0">
              <h3 id={titleId} className="text-base sm:text-lg font-semibold text-gray-900">
                {title}
              </h3>
              {message && (
                <div id={messageId} className="mt-1.5 text-sm text-gray-600 break-words">
                  {message}
                </div>
              )}
              {input && (
                <div className="mt-4">
                  {input.label && (
                    <label className="block text-sm font-medium text-gray-700 mb-1">{input.label}</label>
                  )}
                  {input.multiline ? (
                    <textarea
                      ref={inputRef}
                      rows={3}
                      value={value}
                      placeholder={input.placeholder}
                      onChange={(e) => setValue(e.target.value)}
                      className={inputClass}
                    />
                  ) : (
                    <input
                      ref={inputRef}
                      type="text"
                      value={value}
                      placeholder={input.placeholder}
                      onChange={(e) => setValue(e.target.value)}
                      className={inputClass}
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="px-5 sm:px-6 py-4 bg-gray-50 rounded-b-none sm:rounded-b-xl border-t border-gray-100 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-400"
            >
              {cancelText}
            </button>
            <button
              ref={confirmRef}
              type="submit"
              disabled={!canConfirm}
              className={`w-full sm:w-auto px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${styles.button}`}
            >
              {confirmText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConfirmDialog;
